import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantApi, type SupportConversationSummary } from "@/lib/api/client";
import { usePortalEvent } from "@/lib/portal-socket";

/** Shared key so the Support nav badge and the Support page dedupe onto one
 *  request and always agree on the count. */
export const SUPPORT_CONVERSATIONS_KEY = ["support-conversations"];

/** The member's conversations, with their unread counts.
 *
 *  Doorbell rings and other hub notifications are delivered into a per-user
 *  "Notifications" thread (backend ``comms.channels.portal_chat``), so unread
 *  here covers both real support replies and notifications.
 *
 *  Polled slowly as a safety net; the realtime portal socket refetches the
 *  moment a message lands, same pattern as announcements.
 */
export function useSupportConversations() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: SUPPORT_CONVERSATIONS_KEY,
    queryFn: () => tenantApi.listSupportConversations().then((r) => r.data),
    refetchInterval: 60_000,
    staleTime: 0,
  });

  // A delivered notification appends a message to the thread — refetch so the
  // badge appears without waiting out the poll.
  usePortalEvent("support_message", () => {
    qc.invalidateQueries({ queryKey: SUPPORT_CONVERSATIONS_KEY });
  });

  return query;
}

/** Total unread across every thread — what the Support nav badge shows. */
export function useSupportUnreadTotal(): number {
  const { data } = useSupportConversations();
  return useMemo(
    () => (data ?? []).reduce((sum: number, c: SupportConversationSummary) => sum + (c.unread_count ?? 0), 0),
    [data],
  );
}
